import React from "react";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import MistakeNotesAndReplacement from "./MistakeNotesAndReplacement";

/**
 * The "special states" block for an existing segment — mistake tracking,
 * cancellation tracking, and road-trip exclusion. Edit-only today
 * (these states logically apply to existing bookings), lives here so
 * Phase 5 can drop it into the unified `<SegmentFormDialog>` behind a
 * `mode === 'edit'` guard.
 *
 * Behavior:
 *   - Cancellation and mistake are mutually exclusive — toggling one on
 *     toggles the other off, AND clears its companion notes +
 *     replacement_segment_id so the form save handler doesn't smuggle
 *     stale values back to the backend.
 *   - The Road-trip exclusion checkbox is hidden when the segment IS
 *     the car_rental (the container can't exclude itself).
 *
 * Props:
 *   formData     — the segment form state object (read).
 *   onChange     — patch callback `(patch) => void`. Receives the
 *                  minimum patch needed (preserves single-source-of-
 *                  truth — the caller still owns the state).
 *   allSegments  — list passed to `<MistakeNotesAndReplacement>` so the
 *                  "Replacement segment" dropdown can populate.
 */
export const SegmentSpecialStatesBlock = ({ formData, onChange, allSegments }) => (
  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
    <div className="space-y-3">
      {/* Road trip opt-out — relevant whenever a multi-day car rental
          exists on the trip. Kept lightweight (no rental detection
          here; the checkbox simply records user intent and
          ItineraryView / GadjetView filter on it). Hidden for
          car_rental itself since that's the container, not a child. */}
      {formData.type !== "car_rental" && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="exclude-from-road-trip"
            data-testid="exclude-from-road-trip-checkbox"
            checked={!!formData.exclude_from_road_trip}
            onCheckedChange={(checked) => onChange({ exclude_from_road_trip: !!checked })}
          />
          <Label htmlFor="exclude-from-road-trip" className="text-sm font-medium">
            Exclude from road trip grouping
            <span className="block text-xs font-normal text-gray-500 dark:text-slate-400">
              Prevents this segment from being nested under a multi-day car rental it overlaps.
            </span>
          </Label>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="is-cancelled"
            data-testid="is-cancelled-checkbox"
            checked={formData.is_cancelled}
            onCheckedChange={(checked) =>
              onChange({
                is_cancelled: checked,
                // Mutually exclusive with mistake — flipping cancelled
                // on auto-disables mistake AND clears its notes so the
                // backend doesn't see stale data.
                is_mistake: checked ? false : formData.is_mistake,
                cancellation_notes: checked ? formData.cancellation_notes : "",
                replacement_segment_id: checked ? formData.replacement_segment_id : "",
              })
            }
          />
          <Label htmlFor="is-cancelled" className="text-sm font-medium">
            Mark as Cancelled
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="is-mistake"
            data-testid="is-mistake-checkbox"
            checked={formData.is_mistake}
            onCheckedChange={(checked) =>
              onChange({
                is_mistake: checked,
                is_cancelled: checked ? false : formData.is_cancelled,
                mistake_notes: checked ? formData.mistake_notes : "",
                replacement_segment_id: checked ? formData.replacement_segment_id : "",
              })
            }
          />
          <Label htmlFor="is-mistake" className="text-sm font-medium">
            Mark as Travel Mistake (with financial impact)
          </Label>
        </div>
      </div>

      {formData.is_cancelled && (
        <div className="ml-6 space-y-3 bg-blue-50 dark:bg-blue-950/30 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
          <MistakeNotesAndReplacement
            notesValue={formData.cancellation_notes}
            onNotesChange={(value) => onChange({ cancellation_notes: value })}
            notesLabel="Cancellation Reason"
            notesId="cancellation-notes"
            notesPlaceholder="Example: Plans changed, booking cancelled and refunded..."
            replacementValue={formData.replacement_segment_id}
            onReplacementChange={(value) => onChange({ replacement_segment_id: value })}
            replacementId="replacement-segment-cancelled"
            allSegments={allSegments}
          />
        </div>
      )}

      {formData.is_mistake && (
        <div className="ml-6 space-y-3 bg-red-50 dark:bg-red-950/30 p-3 rounded-lg">
          <MistakeNotesAndReplacement
            notesValue={formData.mistake_notes}
            onNotesChange={(value) => onChange({ mistake_notes: value })}
            replacementValue={formData.replacement_segment_id}
            onReplacementChange={(value) => onChange({ replacement_segment_id: value })}
            allSegments={allSegments}
          />
        </div>
      )}
    </div>
  </div>
);

export default SegmentSpecialStatesBlock;
